import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Avatar,
  Snackbar,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Add,
  Search,
  MoreVert,
  Edit,
  Warning,
  CheckCircle,
  LocalLaundryService,
  Restaurant,
  Spa,
} from '@mui/icons-material';
import InventoryItemDialog from '../components/InventoryItemDialog';

const mockInventory = [
  {
    id: 'INV001',
    name: 'Bed Linens (Queen)',
    category: 'Linens',
    quantity: 150,
    minStock: 100,
    unit: 'sets',
    supplier: 'Luxury Textiles Co.',
    lastRestocked: '2025-11-10',
    cost: 45,
  },
  {
    id: 'INV002',
    name: 'Bath Towels',
    category: 'Linens',
    quantity: 320,
    minStock: 200,
    unit: 'pieces',
    supplier: 'Luxury Textiles Co.',
    lastRestocked: '2025-11-12',
    cost: 12,
  },
  {
    id: 'INV003',
    name: 'Toilet Paper',
    category: 'Supplies',
    quantity: 45,
    minStock: 100,
    unit: 'rolls',
    supplier: 'Hotel Supplies Inc.',
    lastRestocked: '2025-11-05',
    cost: 2,
  },
  {
    id: 'INV004',
    name: 'Shampoo Bottles',
    category: 'Toiletries',
    quantity: 280,
    minStock: 150,
    unit: 'bottles',
    supplier: 'Beauty Essentials',
    lastRestocked: '2025-11-14',
    cost: 3,
  },
  {
    id: 'INV005',
    name: 'Minibar Sodas',
    category: 'Minibar',
    quantity: 85,
    minStock: 120,
    unit: 'cans',
    supplier: 'Beverage Distributors',
    lastRestocked: '2025-11-08',
    cost: 1.5,
  },
  {
    id: 'INV006',
    name: 'Cleaning Detergent',
    category: 'Cleaning',
    quantity: 35,
    minStock: 50,
    unit: 'bottles',
    supplier: 'CleanPro Solutions',
    lastRestocked: '2025-11-11',
    cost: 8,
  },
  {
    id: 'INV007',
    name: 'Coffee Pods',
    category: 'Food & Beverage',
    quantity: 450,
    minStock: 200,
    unit: 'pods',
    supplier: 'Coffee Masters',
    lastRestocked: '2025-11-15',
    cost: 0.5,
  },
  {
    id: 'INV008',
    name: 'Hand Soap',
    category: 'Toiletries',
    quantity: 180,
    minStock: 150,
    unit: 'bottles',
    supplier: 'Beauty Essentials',
    lastRestocked: '2025-11-13',
    cost: 4,
  },
];

export default function Inventory() {
  const [items, setItems] = React.useState(mockInventory);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState('add');
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const filteredItems = items.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query) ||
      item.id.toLowerCase().includes(query) ||
      item.supplier.toLowerCase().includes(query)
    );
  });

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleAddItem = () => {
    setSelectedItem(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleEditItem = () => {
    setDialogMode('edit');
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveItem = (itemData) => {
    if (dialogMode === 'add') {
      setItems([...items, itemData]);
      setSnackbar({
        open: true,
        message: `Item ${itemData.name} added successfully!`,
        severity: 'success',
      });
    } else {
      setItems(items.map(i => i.id === itemData.id ? itemData : i));
      setSnackbar({
        open: true,
        message: `Item ${itemData.name} updated successfully!`,
        severity: 'success',
      });
    }
  };

  const handleDeleteItem = () => {
    if (selectedItem) {
      setItems(items.filter(i => i.id !== selectedItem.id));
      setSnackbar({
        open: true,
        message: `Item ${selectedItem.name} removed successfully!`,
        severity: 'success',
      });
      setAnchorEl(null);
    }
  };

  const getStockStatus = (item) => {
    const percentage = (item.quantity / item.minStock) * 100;
    if (percentage <= 50) return { label: 'Critical', color: 'error' };
    if (percentage <= 100) return { label: 'Low', color: 'warning' };
    return { label: 'Good', color: 'success' };
  };

  const lowStockItems = items.filter(i => i.quantity <= i.minStock);
  const criticalItems = items.filter(i => i.quantity <= i.minStock * 0.5);
  const totalValue = items.reduce((sum, i) => sum + (i.quantity * i.cost), 0);

  const stats = [
    { label: 'Total Items', value: items.length, color: '#1976d2', icon: InventoryIcon },
    { label: 'Low Stock', value: lowStockItems.length, color: '#ed6c02', icon: Warning },
    { label: 'Critical Stock', value: criticalItems.length, color: '#d32f2f', icon: Warning },
    { 
      label: 'Total Value', 
      value: `$${totalValue.toLocaleString()}`, 
      color: '#2e7d32',
      icon: CheckCircle,
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Inventory Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track and manage hotel supplies, linens, and amenities
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAddItem}>
          Add Item
        </Button>
      </Box>

      <Grid container spacing={3} mb={3}>
        {stats.map((stat) => {
          const IconComponent = stat.icon;
          return (
            <Grid item xs={6} md={3} key={stat.label}>
              <Card sx={{ bgcolor: `${stat.color}10`, borderLeft: `4px solid ${stat.color}` }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                      <Typography variant="h4" fontWeight="bold" color={stat.color}>
                        {stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 48, height: 48 }}>
                      <IconComponent />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {criticalItems.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff3e0', borderLeft: '4px solid #ed6c02' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            <Typography variant="body2" fontWeight="bold">
              {criticalItems.length} item(s) critically low on stock! Restock immediately.
            </Typography>
          </Box>
        </Paper>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by item name, category, supplier, or ID..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Item ID</strong></TableCell>
              <TableCell><strong>Item Name</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Stock Level</strong></TableCell>
              <TableCell><strong>Quantity</strong></TableCell>
              <TableCell><strong>Min Stock</strong></TableCell>
              <TableCell><strong>Unit Cost</strong></TableCell>
              <TableCell><strong>Supplier</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.map((item) => {
              const status = getStockStatus(item);
              const stockPercentage = Math.min((item.quantity / item.minStock) * 100, 100);
              
              return (
                <TableRow key={item.id} hover>
                  <TableCell><strong>{item.id}</strong></TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    <Chip label={item.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={stockPercentage}
                        color={status.color}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <strong>{item.quantity}</strong> {item.unit}
                  </TableCell>
                  <TableCell>{item.minStock} {item.unit}</TableCell>
                  <TableCell>${item.cost}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {item.supplier}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      size="small"
                      color={status.color}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => handleMenuClick(e, item)}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={handleEditItem}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Item
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedItem) {
            setItems(items.map(i => 
              i.id === selectedItem.id 
                ? { ...i, quantity: i.quantity + 50, lastRestocked: new Date().toISOString().split('T')[0] }
                : i
            ));
            setSnackbar({
              open: true,
              message: `${selectedItem.name} restocked successfully!`,
              severity: 'success',
            });
          }
          setAnchorEl(null);
        }}>
          <CheckCircle fontSize="small" sx={{ mr: 1 }} />
          Restock Item
        </MenuItem>
        <MenuItem onClick={handleDeleteItem} sx={{ color: 'error.main' }}>
          <Warning fontSize="small" sx={{ mr: 1 }} />
          Remove Item
        </MenuItem>
      </Menu>

      <InventoryItemDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        item={selectedItem}
        onSave={handleSaveItem}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
